import React, { useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import CloseButton from './CloseButton';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string | React.ReactNode;
  subtitle?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  footer?: React.ReactNode;
  className?: string;
  contentClassName?: string;
  mobileFullScreen?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  maxWidth = 'md',
  showCloseButton = true,
  footer,
  className = '',
  contentClassName = '',
  mobileFullScreen = true
}) => {
  const { theme } = useTheme();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const maxWidthClasses = {
    xs: 'max-w-xs',
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full'
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100000] bg-black/50"
            onClick={onClose}
          />

          {/* Modal Container — respects iOS safe-area (notch + home indicator) */}
          <div
            className="fixed inset-0 z-[100001] overflow-y-auto"
            style={{
              paddingTop: 'max(20px, env(safe-area-inset-top))',
              paddingBottom: 'max(70px, calc(70px + env(safe-area-inset-bottom)))',
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)',
            }}
          >
            <div className={`flex min-h-full ${mobileFullScreen ? 'items-stretch sm:items-center' : 'items-center'} justify-center ${mobileFullScreen ? 'p-0 sm:p-4' : 'p-4'}`}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className={`
                  relative w-full ${maxWidthClasses[maxWidth]}
                  ${mobileFullScreen ? 'h-full sm:h-auto' : ''}
                  ${className}
                `}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  className={`
                    bg-white rounded-t-2xl sm:rounded-xl shadow-xl
                    border border-gray-200 overflow-hidden
                    ${mobileFullScreen ? 'h-full sm:h-auto sm:min-h-0' : ''}
                    flex flex-col
                  `}
                  style={{
                    maxHeight: mobileFullScreen
                      ? 'calc(100vh - 90px - env(safe-area-inset-top) - env(safe-area-inset-bottom))'
                      : 'calc(90vh - 40px)',
                  }}
                >
                  {/* Header */}
                  {(title || subtitle || showCloseButton) && (
                    <div
                      className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-gray-200"
                      style={{
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                      }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 pr-3">
                          {title && (
                            <h2
                              className="text-base sm:text-lg font-semibold"
                              style={{ color: 'var(--text-primary)' }}
                            >
                              {title}
                            </h2>
                          )}
                          {subtitle && (
                            <p
                              className="text-xs sm:text-sm font-medium mt-1"
                              style={{ color: 'var(--text-tertiary)' }}
                            >
                              {subtitle}
                            </p>
                          )}
                        </div>
                        {showCloseButton && (
                          <CloseButton onClick={onClose} size="md" className="-mt-1 -mr-1" />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div
                    className={`
                      flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6
                      ${contentClassName}
                    `}
                    style={{
                      background: 'white',
                      color: theme.text,
                    }}
                  >
                    {children}
                  </div>

                  {/* Footer */}
                  {footer && (
                    <div
                      className="flex-shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-200"
                      style={{
                        background: 'var(--bg-sunken)',
                      }}
                    >
                      {footer}
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Modal;
